import type { GetStaticProps, NextPage } from 'next';
import {
  Box,
  Link,
  ResponsiveSection,
  Stack,
  Text,
} from '@stitch-fix/mode-react';
import { ContentGallery } from '../../components/ContentGallery';
import { GetStartedBox } from '../../components/GetStartedBox';
import { Header } from '../../components/Header';
import { ImageTestimonials } from '../../components/ImageTestimonials/ImageTestimonials';
import { Layout } from '../../components/Layout';
import { PrimaryObjectivesHighlighted } from '../../components/PrimaryObjectivesHighlighted/PrimaryObjectivesHighlighted';
import { SEOData } from '../../components/SEOData';
import { StepsWithIllustrations } from '../../components/StepsWithIllustrations/StepsWithIllustrations';
import { TrigallHero } from '../../components/TrigallHero';
import { serverLogger } from '../../utils/loggers/server';
import { getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { HowItWorksPageProps, howItWorksSchema } from './schema';

export const getStaticProps: GetStaticProps<HowItWorksPageProps> = async () => {
  try {
    const entry = await getCachedEntry({
      contentType: 'how_it_works',
      schema: howItWorksSchema,
      language: 'en-US',
      logger: serverLogger,
    });

    return {
      props: entry.data,
      revalidate: 300,
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Failed to fetch required page data - /how-it-works',
      context: {
        error,
        page: '/how-it-works',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render /how-it-works page');
  }
};

const HowItWorksPage: NextPage<HowItWorksPageProps> = props => {
  return (
    <Layout>
      <ResponsiveSection mt="0" width={{ sm: 'full-bleed' }} gutter="none">
        <SEOData {...props.seoData} />
        <Stack
          mb={{ sm: '3rem', lg: '4rem' }}
          spacing={{ sm: '3rem', lg: '4rem' }}
        >
          <TrigallHero {...props.hero} />
          <Box>
            <Header {...props.header} />
            <StepsWithIllustrations {...props.howItWorks} />
          </Box>
          <ImageTestimonials {...props.testimonials} />
          <PrimaryObjectivesHighlighted {...props.objectives} />
          <Box
            mt={{ sm: '-1rem', lg: '-2rem' }}
            style={{ textAlign: 'center' }}
          >
            <Text setting="body-small">
              Learn more about <Link href="/pricing">our prices</Link>
            </Text>
          </Box>
          <ContentGallery {...props.contentGallery} />
          <GetStartedBox />
        </Stack>
      </ResponsiveSection>
    </Layout>
  );
};

export default HowItWorksPage;
